
import { useUpdateNotificationSetting, type NotificationSetting } from "@/hooks/useNotificationSettings";
import { TableCell, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useDebouncedCallback } from "use-debounce";
import { NotificationChannelSelector } from "./NotificationChannelSelector";

interface NotificationSettingRowProps {
  setting: NotificationSetting;
}

const statusLabels: Record<string, string> = {
  'waiting': 'عملاء جدد',
  'pricing_requested': 'طلب تسعير',
  'pricing_sent': 'تم إرسال السعر',
  'booking_confirmed': 'تأكيد الحجز',
  'traveling': 'قيد السفر',
  'customer_service': 'خدمة العملاء',
  'confirmed': 'مؤكد',
  'in_progress': 'قيد التنفيذ',
  'waiting_supplier': 'بانتظار المورد',
  'cancelled': 'ملغي'
};

export const NotificationSettingRow = ({ setting }: NotificationSettingRowProps) => {
  const updateSettingMutation = useUpdateNotificationSetting();

  const debouncedUpdate = useDebouncedCallback((field: keyof NotificationSetting, value: any) => {
    updateSettingMutation.mutate({ id: setting.id, [field]: value });
  }, 1000);

  const handleChannelsChange = (channels: string[]) => {
    debouncedUpdate('channels', channels);
  };

  return (
    <TableRow key={setting.id}>
      <TableCell className="font-medium">
        {statusLabels[setting.client_status] || setting.client_status}
      </TableCell>
      <TableCell>
        <Switch
          checked={setting.is_active}
          onCheckedChange={(checked) => debouncedUpdate('is_active', checked)}
          disabled={updateSettingMutation.isPending}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={setting.notify_creator}
          onCheckedChange={(checked) => debouncedUpdate('notify_creator', checked)}
          disabled={updateSettingMutation.isPending}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={setting.notify_assigned_sales}
          onCheckedChange={(checked) => debouncedUpdate('notify_assigned_sales', checked)}
          disabled={updateSettingMutation.isPending}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={setting.notify_assigned_service}
          onCheckedChange={(checked) => debouncedUpdate('notify_assigned_service', checked)}
          disabled={updateSettingMutation.isPending}
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          defaultValue={setting.delay_minutes ?? ''}
          onChange={(e) => debouncedUpdate('delay_minutes', parseInt(e.target.value) || null)}
          className="w-24"
          placeholder="فوري"
          disabled={updateSettingMutation.isPending}
        />
      </TableCell>
      <TableCell className="min-w-[300px]">
        <Input
          defaultValue={setting.message_template ?? ''}
          onChange={(e) => debouncedUpdate('message_template', e.target.value)}
          placeholder="رسالة افتراضية"
          disabled={updateSettingMutation.isPending}
        />
      </TableCell>
      <TableCell>
        <NotificationChannelSelector
          channels={setting.channels || ['in_app']}
          onChange={handleChannelsChange}
          disabled={updateSettingMutation.isPending}
        />
      </TableCell>
    </TableRow>
  );
};
